import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { TEXT_SIZE_CLASS, type Scale } from "./tokens";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  size?: Scale;
  /** El wrapper (label+input+error) es un `<div>`; pasa clases para ese contenedor. */
  wrapperClassName?: string;
}

const HEIGHT_CLASS = {
  xs: "h-7",
  sm: "h-8",
  md: "h-9",
  lg: "h-10",
  xl: "h-12",
} as const;

/** Reemplazo de `<TextInput>` de Mantine · label + input + mensaje de error/ayuda. */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      description,
      error,
      leftSection,
      rightSection,
      size = "sm",
      required,
      className,
      wrapperClassName,
      id,
      ...props
    },
    ref
  ) => {
    const autoId = id ?? props.name;
    return (
      <div className={cn("flex flex-col gap-1", wrapperClassName)}>
        {label && (
          <label htmlFor={autoId} className="text-sm font-medium text-foreground">
            {label}
            {required && <span className="text-peligro-600"> *</span>}
          </label>
        )}
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
        <div className="relative flex items-center">
          {leftSection && (
            <span className="pointer-events-none absolute left-2.5 flex items-center text-muted-foreground">
              {leftSection}
            </span>
          )}
          <input
            ref={ref}
            id={autoId}
            className={cn(
              "w-full rounded-md border bg-background transition-[border-color,box-shadow] ease-out-quint duration-140",
              "focus:outline-none focus:ring-2 focus:ring-multiva-600/30 focus:border-multiva-600",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "placeholder:text-muted-foreground",
              error ? "border-peligro-600" : "border-input",
              HEIGHT_CLASS[size],
              TEXT_SIZE_CLASS[size],
              leftSection ? "pl-9" : "pl-3",
              rightSection ? "pr-9" : "pr-3",
              className
            )}
            {...props}
          />
          {rightSection && (
            <span className="absolute right-2.5 flex items-center text-muted-foreground">
              {rightSection}
            </span>
          )}
        </div>
        {error && typeof error !== "boolean" && (
          <p className="text-xs text-peligro-600">{error}</p>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";
